'use client';

import { client } from '#client';
import { betaCallValidation } from '../../../server/routers/public/beta-call-validation';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@banzai/ui/components/form';
import { FormFieldWrapper } from '@banzai/ui/components/form-field-wrapper';
import { FormSwitchWrapper } from '@banzai/ui/components/form-switch-wrapper';
import { Button } from '@banzai/ui/components/button';
import '@banzai/data/util';
import { FaCheckCircle } from 'react-icons/fa';
import { H4 } from '@banzai/ui/components/typography';
import { cn } from '@banzai/ui/lib/utils';

const zodObject = z.object(betaCallValidation);

function SuccessMessage({ onStartOver }: { onStartOver: () => void }) {
  return (
    <div className="flex flex-col items-center space-y-6 py-4">
      <FaCheckCircle className="text-primary animate-jump-in" size="80px" />
      <H4>You will be receiving your call shortly!</H4>
      <Button className="rounded-full font-normal" onClick={onStartOver}>
        Start Over
      </Button>
    </div>
  );
}

export function BetaCallForm() {
  const betaCallMutation = client.public.betaCall.useMutation();

  const form = useForm<z.infer<typeof zodObject>>({
    mode: 'onChange',
    resolver: zodResolver(zodObject),
  });

  async function onSubmit(obj: z.infer<typeof zodObject>) {
    await betaCallMutation.mutateAsync(obj);
  }

  function startOver() {
    form.reset();
    betaCallMutation.reset();
  }

  const formDisabled = betaCallMutation.isPending || betaCallMutation.isSuccess;

  return (
    <div className={cn('sm:flex sm:items-center sm:flex-col w-full')}>
      {((betaCallMutation.isSuccess || false) && <SuccessMessage onStartOver={startOver} />) || (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col items-stretch sm:w-[500px] space-y-4 p-1">
            <div className="sm:grid sm:grid-cols-2 sm:gap-x-4 sm:gap-y-0 gap-y-4 flex flex-col">
              <FormFieldWrapper control={form.control} name="name" label="Your name" required />
              <FormFieldWrapper
                control={form.control}
                name="phone"
                label="Your phone number"
                required
                inputType="tel"
              />
            </div>

            <FormSwitchWrapper
              control={form.control}
              name="optin"
              label="Confirm"
              description="I agree to receive a phone call and SMS based on my data."
            />

            <Button type="submit" className="rounded-full font-normal" disabled={formDisabled}>
              Call me!
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
}
